import React, { useState, useCallback, useEffect } from 'react';
import { Card, Form, Button, Alert, Row, Col, Badge } from 'react-bootstrap';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Leaflet icon fix
const pinIcon = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = pinIcon;

// --- Storage ---
const SETTINGS_KEY = 'smartcart_settings';

interface AppSettings {
  home_lat: number | null;
  home_lng: number | null;
  search_radius_m: number;
  products_per_concept: number;
}

const DEFAULTS: AppSettings = { home_lat: null, home_lng: null, search_radius_m: 3000, products_per_concept: 5 };
const VIENNA: [number, number] = [48.2082, 16.3738];

function loadSettings(): AppSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { ...DEFAULTS };
}

function persistSettings(s: AppSettings) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
}

// --- Map sub-components ---

/** Flies the map to a new center when `target` changes. */
const FlyTo: React.FC<{ target: [number, number]; zoom?: number }> = ({ target, zoom }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo(target, zoom ?? map.getZoom(), { duration: 0.8 });
  }, [map, target, zoom]);
  return null;
};

/** Captures clicks on the map. */
const ClickCapture: React.FC<{ onClick: (lat: number, lng: number) => void }> = ({ onClick }) => {
  useMapEvents({ click(e) { onClick(e.latlng.lat, e.latlng.lng); } });
  return null;
};

// --- Component ---

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<AppSettings>(loadSettings);
  const [toast, setToast] = useState<string | null>(null);

  const hasHome = settings.home_lat !== null && settings.home_lng !== null;
  const mapTarget: [number, number] = hasHome
    ? [settings.home_lat!, settings.home_lng!]
    : VIENNA;

  // Auto-save whenever settings change
  const update = useCallback((patch: Partial<AppSettings>) => {
    setSettings(prev => {
      const next = { ...prev, ...patch };
      persistSettings(next);
      return next;
    });
    setToast('Gespeichert');
    setTimeout(() => setToast(null), 1500);
  }, []);

  const handleMapClick = (lat: number, lng: number) => {
    update({ home_lat: lat, home_lng: lng });
  };

  const handleGPS = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => update({ home_lat: pos.coords.latitude, home_lng: pos.coords.longitude }),
      () => setToast('Standortzugriff verweigert'),
    );
  };

  const handleClear = () => {
    update({ home_lat: null, home_lng: null });
  };

  return (
    <>
      <h4 className="mb-3">Einstellungen</h4>

      {toast && (
        <Alert variant="success" className="py-1 px-3" style={{ position: 'fixed', top: 70, right: 20, zIndex: 9999 }}>
          {toast}
        </Alert>
      )}

      {/* Home Location */}
      <Card className="mb-3">
        <Card.Body>
          <Card.Title className="mb-1">Home-Standort</Card.Title>
          <p className="text-muted small mb-3">
            Klicke auf die Karte um deinen Standort zu setzen, oder nutze GPS.
            Der Standort wird sofort gespeichert und für Preisvergleiche und Routen verwendet.
          </p>

          <div style={{ height: '350px', borderRadius: '8px', overflow: 'hidden' }} className="mb-3">
            <MapContainer center={mapTarget} zoom={hasHome ? 15 : 12} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              <ClickCapture onClick={handleMapClick} />
              <FlyTo target={mapTarget} zoom={hasHome ? 15 : 12} />
              {hasHome && (
                <Marker position={[settings.home_lat!, settings.home_lng!]}>
                  <Popup>Dein Home-Standort</Popup>
                </Marker>
              )}
            </MapContainer>
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline-primary" onClick={handleGPS}>
              GPS-Standort verwenden
            </Button>
            {hasHome ? (
              <>
                <Badge bg="success" className="px-2 py-1">
                  Gespeichert: {settings.home_lat!.toFixed(4)}, {settings.home_lng!.toFixed(4)}
                </Badge>
                <Button size="sm" variant="outline-danger" onClick={handleClear}>
                  Entfernen
                </Button>
              </>
            ) : (
              <Badge bg="warning" text="dark" className="px-2 py-1">
                Kein Standort gesetzt
              </Badge>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Search Radius */}
      <Card className="mb-3">
        <Card.Body>
          <Card.Title className="mb-1">Suchradius</Card.Title>
          <p className="text-muted small mb-3">
            Wie weit sollen Märkte von deinem Standort entfernt sein?
          </p>
          <Row className="align-items-center">
            <Col xs={8} md={6}>
              <Form.Range
                min={500}
                max={10000}
                step={500}
                value={settings.search_radius_m}
                onChange={(e) => update({ search_radius_m: Number(e.target.value) })}
              />
            </Col>
            <Col xs={4} md={2}>
              <Badge bg="secondary" className="fs-6">
                {settings.search_radius_m >= 1000
                  ? `${(settings.search_radius_m / 1000).toFixed(1)} km`
                  : `${settings.search_radius_m} m`}
              </Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Products per Concept */}
      <Card className="mb-3">
        <Card.Body>
          <Card.Title className="mb-1">Produkte pro Konzept</Card.Title>
          <p className="text-muted small mb-3">
            Wie viele Produkte sollen pro Konzeptgruppe im Preisvergleich angezeigt werden?
          </p>
          <Row className="align-items-center">
            <Col xs={8} md={6}>
              <Form.Range
                min={3}
                max={10}
                step={1}
                value={settings.products_per_concept}
                onChange={(e) => update({ products_per_concept: Number(e.target.value) })}
              />
            </Col>
            <Col xs={4} md={2}>
              <Badge bg="secondary" className="fs-6">
                {settings.products_per_concept} Produkte
              </Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default Settings;
